import {Fragment} from 'react';
import {Pencil, Trash2} from 'lucide-react';
import {Button} from '@ui/shadcn/button';
import {ConfirmActionButton} from '@ui/local/confirm-action-button';
import {formatDateTime} from '@lib/utils';
import type {Task, TaskComment} from '@api/tasks/types';
import {splitCommentMentions} from '../model/mentions';
import {useTaskComments} from '../model/useTaskComments';
import {useTaskCommentActions} from '../model/useTaskCommentActions';
import {TaskCommentEditor} from './TaskCommentEditor';

const EditComment = ({
  task,
  comment,
  onClose,
}: {
  task: Task;
  comment: TaskComment;
  onClose: () => void;
}) => {
  const form = useTaskComments(task, true, false, {comment, onSaved: onClose});
  return (
    <TaskCommentEditor form={form} onCancel={onClose} author={comment.author} />
  );
};

/** Комментарий с собственными правами, встроенным редактором и подтверждением удаления. */
export const TaskCommentItem = ({
  task,
  comment,
}: {
  task: Task;
  comment: TaskComment;
}) => {
  const actions = useTaskCommentActions(task.id, comment);
  const names = comment.mentions.map(
    (sid) =>
      comment.mentionUsers?.find((person) => person.userSid === sid)
        ?.displayName ?? sid,
  );
  const timestamp = (
    <span className='ml-auto shrink-0 whitespace-nowrap text-[10px] leading-4 text-[var(--color-muted-foreground)]'>
      {formatDateTime(comment.createdAt)}
      {comment.updatedAt && (
        <span title={`Изменён ${formatDateTime(comment.updatedAt)}`}>
          {' '}
          · изменён
        </span>
      )}
    </span>
  );
  return (
    <div className='bg-[var(--color-muted)] border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 space-y-1'>
      {!actions.editing && (
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <span className='text-xs font-semibold'>{comment.author}</span>
          <div className='flex items-center gap-0'>
            {!actions.editing && (
              <>
                {comment.canEdit === true && (
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='size-6'
                    aria-label='Редактировать комментарий'
                    title='Редактировать'
                    disabled={actions.isDeleting}
                    onClick={() => actions.setEditing(true)}
                  >
                    <Pencil size={13} />
                  </Button>
                )}
                {comment.canDelete === true && (
                  <ConfirmActionButton
                    variant='ghost'
                    confirmVariant='destructive'
                    size='icon'
                    className='size-6'
                    ariaLabel='Удалить комментарий'
                    initialContent={<Trash2 size={13} />}
                    confirmContent={<Trash2 size={13} />}
                    confirmTooltipContent='Нажмите ещё раз, чтобы удалить комментарий'
                    loading={actions.isDeleting}
                    onConfirm={actions.deleteComment}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
      {actions.editing ? (
        <>
          <EditComment
            task={task}
            comment={comment}
            onClose={() => actions.setEditing(false)}
          />
          <div className='flex justify-end'>{timestamp}</div>
        </>
      ) : (
        <div className='flex flex-wrap items-end gap-x-3 gap-y-1'>
          <div className='min-w-0 flex-[1_1_200px] text-xs leading-relaxed whitespace-pre-wrap break-words'>
            {splitCommentMentions(comment.text, names).map((part, i) =>
              part.mention ? (
                <span
                  key={i}
                  className='text-[var(--color-primary)] font-semibold'
                >
                  {part.text}
                </span>
              ) : (
                <Fragment key={i}>{part.text}</Fragment>
              ),
            )}
          </div>
          {timestamp}
        </div>
      )}
    </div>
  );
};
